// Team invoices (finance consolidation, the user 2026-10-22): one person's claims to the agency, Draft, Received and Paid,
// which replace Pay as the payout, and /finance's Invoices list of client and team invoices in one. Admin only: the admin
// apiClient, as every finance service. It throws an HttpError carrying the server's message on a 4xx (TD-093, TD-102).
import { apiClient } from "@/services/apiClient";
import type {
  AgencyInvoiceListResponse, TeamInvoice, TeamInvoiceCandidates, TeamInvoiceClaimInput, TeamInvoiceCreateInput, TeamInvoiceDetail, TeamInvoiceNewJobInput, TeamInvoicePayoutInput,
  TeamInvoiceStatusInput, TeamPayAnswer,
} from "../types";

const BASE = "/api/finance";

/** What the claims ticked add up to, as the server sums them. */
export interface TeamClaimsTotal {
  totalMinor: number;
}

export const teamInvoiceService = {
  /** Both kinds in one list, each tab's count, and each kind's totals apart. */
  list: () => apiClient.get<AgencyInvoiceListResponse>(`${BASE}/agency/invoices`),
  detail: (invoiceId: string) => apiClient.get<TeamInvoiceDetail>(`${BASE}/agency/team-invoices/${invoiceId}`),
  /** What New team invoice offers a person: their draft, if any, their owed claims and the jobs to claim on. */
  candidates: (personId: string) => apiClient.get<TeamInvoiceCandidates>(`${BASE}/agency/team-invoices/new?${new URLSearchParams({ person: personId }).toString()}`),
  /** The claims named, comma-separated, as the server sums them. */
  claimsTotal: (personId: string, claimIds: string[]) =>
    apiClient.get<TeamClaimsTotal>(`${BASE}/agency/team-invoices/claims-total?${new URLSearchParams({ person: personId, claimIds: claimIds.join(",") }).toString()}`),
  /** Their draft if one exists, else a new one of the claims named (or of their open claims). */
  create: (input: TeamInvoiceCreateInput) => apiClient.post<TeamInvoice>(`${BASE}/agency/team-invoices`, input),
  /** The Team tab's Pay: their draft, or a new one, or none when nothing is owed. */
  open: (personId: string) => apiClient.post<TeamPayAnswer>(`${BASE}/agency/team-invoices/open`, { personId }),
  /** Their open claims onto a draft. */
  addClaims: (invoiceId: string, claimIds: string[]) => apiClient.post<TeamInvoice>(`${BASE}/agency/team-invoices/${invoiceId}/claims`, { claimIds }),
  /** Off the draft; the claim stays on its job. */
  removeClaim: (invoiceId: string, claimId: string) => apiClient.delete<TeamInvoice>(`${BASE}/agency/team-invoices/${invoiceId}/claims/${claimId}`),
  /** A new claim on a job, on the draft. */
  claim: (invoiceId: string, input: TeamInvoiceClaimInput) => apiClient.post<TeamInvoice>(`${BASE}/agency/team-invoices/${invoiceId}/claim`, input),
  /** A new active job carrying the claim, on the draft, both or neither. */
  newJob: (invoiceId: string, input: TeamInvoiceNewJobInput) => apiClient.post<TeamInvoice>(`${BASE}/agency/team-invoices/${invoiceId}/jobs`, input),
  /** Received stamps today and is refused with no claim; nothing changes once paid. */
  setStatus: (invoiceId: string, input: TeamInvoiceStatusInput) => apiClient.put<TeamInvoice>(`${BASE}/agency/team-invoices/${invoiceId}/status`, input),
  /** On a received one: books the payout, which locks its claims. */
  payout: (invoiceId: string, input: TeamInvoicePayoutInput) => apiClient.post<TeamInvoice>(`${BASE}/agency/team-invoices/${invoiceId}/payout`, input),
};
